import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Inbox, Mail, Sparkles, Users2, Workflow, Package,
  Network, MessageSquareQuote, Megaphone, Settings, LogOut, Globe, Loader2, Building2,
  Headset, ShieldCheck, Info, UserPlus, Briefcase,
} from "lucide-react";
import { api } from "@/lib/api";
import { useContentSaver } from "@/components/admin/kit";
import Analytics from "@/components/admin/Analytics";
import Leads from "@/components/admin/Leads";
import EmailTool from "@/components/admin/EmailTool";
import TeamManager from "@/components/admin/TeamManager";
import TestimonialsManager from "@/components/admin/TestimonialsManager";
import TrustedByManager from "@/components/admin/TrustedByManager";
import ClientCountriesManager from "@/components/admin/ClientCountriesManager";
import ServicesManager from "@/components/admin/ServicesManager";
import SupportInbox from "@/components/admin/SupportInbox";
import TeamAccounts from "@/components/admin/TeamAccounts";
import NotesPanel from "@/components/admin/NotesPanel";
import Recruitment from "@/components/admin/Recruitment";
import SettingsPage from "@/components/admin/SettingsPage";
import { AboutEditor } from "@/components/admin/AboutEditor";
import { HeroManager, TalentManager, ProcessManager, PackagesManager, PromoManager } from "@/components/admin/Managers";

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin"] },
  { key: "support", label: "Support Inbox", icon: Headset, roles: ["admin", "staff"] },
  { key: "leads", label: "Lead Inbox", icon: Inbox, roles: ["admin", "staff"] },
  { key: "email", label: "Email Tool", icon: Mail, roles: ["admin", "limited"] },
  { key: "hero", label: "Hero Manager", icon: Sparkles, roles: ["admin"] },
  { key: "talent", label: "Talent Pool", icon: Users2, roles: ["admin"] },
  { key: "process", label: "Process", icon: Workflow, roles: ["admin"] },
  { key: "services", label: "Services", icon: Briefcase, roles: ["admin"] },
  { key: "packages", label: "Packages", icon: Package, roles: ["admin"] },
  { key: "team", label: "Team (VA Profiles)", icon: Network, roles: ["admin", "staff"] },
  { key: "testimonials", label: "Testimonials", icon: MessageSquareQuote, roles: ["admin"] },
  { key: "trusted", label: "Trusted By Logos", icon: Building2, roles: ["admin"] },
  { key: "countries", label: "Client Countries", icon: Globe, roles: ["admin"] },
  { key: "about", label: "About Us Editor", icon: Info, roles: ["admin"] },
  { key: "recruitment", label: "Recruitment", icon: UserPlus, roles: ["admin", "staff"] },
  { key: "teammgmt", label: "Team Management", icon: ShieldCheck, roles: ["admin"] },
  { key: "promotions", label: "Promotions", icon: Megaphone, roles: ["admin"] },
  { key: "settings", label: "Settings", icon: Settings, roles: ["admin"] },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState(null);
  const [section, setSection] = useState(null);
  const [content, setContent] = useState(null);
  const { saving, save } = useContentSaver();

  const loadContent = useCallback(() => {
    api.get("/content").then(({ data }) => setContent(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => {
        setUser(data);
        const allowed = NAV.filter((n) => n.roles.includes(data.role));
        setSection(allowed[0]?.key || null);
        setChecking(false);
        loadContent();
      })
      .catch(() => navigate("/admin"));
  }, [navigate, loadContent]);

  if (checking || !user) {
    return (
      <div className="min-h-screen bg-[#0A0F2C] flex items-center justify-center">
        <Loader2 size={28} className="text-cyan-400 animate-spin" />
      </div>
    );
  }

  const nav = NAV.filter((n) => n.roles.includes(user.role));
  if (!nav.find((n) => n.key === section)) setSection(nav[0]?.key || null);

  return (
    <div className="min-h-screen bg-[#0A0F2C] flex" data-testid="admin-dashboard">
      <aside className="hidden lg:flex flex-col w-64 border-r border-white/10 bg-[#080D24] p-5 sticky top-0 h-screen">
        <div className="flex items-center gap-2.5 px-2">
          <img src="/logo-full.png" alt="TrustDesk" style={{ height: 36, width: "auto" }} />
        </div>
        <nav className="mt-8 flex-1 space-y-1 overflow-y-auto" data-testid="admin-sidebar">
          {nav.map((n) => (
            <button
              key={n.key}
              onClick={() => setSection(n.key)}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${section === n.key ? "bg-[#00BFFF]/12 text-cyan-300 border border-cyan-400/30" : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"}`}
              data-testid={`admin-sidebar-nav-${n.key}`}
            >
              <n.icon size={16} /> {n.label}
            </button>
          ))}
        </nav>
        <div className="space-y-2 pt-4 border-t border-white/10">
          <a href="/" className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-semibold text-slate-400 hover:text-white hover:bg-white/5 transition-colors">
            <Globe size={16} /> View site
          </a>
          <button
            onClick={async () => { await api.post("/auth/logout"); navigate("/admin"); }}
            className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-semibold text-slate-400 hover:text-red-400 hover:bg-white/5 transition-colors"
            data-testid="admin-logout-button"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="lg:hidden flex items-center justify-between px-5 py-4 border-b border-white/10 bg-[#080D24] sticky top-0 z-10">
          <div className="font-display font-extrabold text-white">TrustDesk Admin</div>
          <div className="flex gap-2">
            <a href="/" className="text-xs font-semibold text-cyan-300">View site</a>
            <button onClick={async () => { await api.post("/auth/logout"); navigate("/admin"); }} className="text-xs font-semibold text-slate-400">Sign out</button>
          </div>
        </div>
        <div className="lg:hidden flex gap-1.5 overflow-x-auto px-4 py-3 border-b border-white/10 bg-[#080D24]">
          {nav.map((n) => (
            <button
              key={n.key}
              onClick={() => setSection(n.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${section === n.key ? "bg-[#00BFFF] text-[#06122B]" : "bg-[#0E1642] text-slate-300 border border-white/10"}`}
            >
              {n.label}
            </button>
          ))}
        </div>

        <div className="p-5 sm:p-8 max-w-6xl">
          {section === "dashboard" && <Analytics />}
          {section === "support" && <SupportInbox />}
          {section === "leads" && <Leads />}
          {section === "email" && <EmailTool limited={user.role === "limited"} />}
          {section === "team" && <TeamManager />}
          {section === "testimonials" && <TestimonialsManager />}
          {section === "trusted" && <TrustedByManager />}
          {section === "countries" && <ClientCountriesManager />}
          {section === "recruitment" && <Recruitment />}
          {section === "teammgmt" && <TeamAccounts currentUser={user} />}
          {section === "settings" && <SettingsPage />}
          {section === "about" && <AboutEditor initial={content?.about} onSave={save} saving={saving} />}
          {section === "hero" && <HeroManager initial={content?.hero} onSave={save} saving={saving} />}
          {section === "talent" && <TalentManager initial={content?.talent} onSave={save} saving={saving} />}
          {section === "process" && <ProcessManager initial={content?.process} onSave={save} saving={saving} />}
          {section === "services" && <ServicesManager />}
          {section === "packages" && <PackagesManager initial={content?.packages} onSave={save} saving={saving} />}
          {section === "promotions" && <PromoManager initial={content?.promo} onSave={save} saving={saving} />}
        </div>
      </main>

      <NotesPanel />
    </div>
  );
}
